import { useState, useEffect, useCallback, useRef } from 'react';
import { useConfig } from '../context/ConfigContext';
import { Plus, Trash2, Settings, Eye, ChevronDown, Sparkles, Edit3, MoreHorizontal, Database, Link2, TestTube } from 'lucide-react';
import { Card, Popconfirm } from 'antd';

// 鉴权配置组件（复用3处）
function AuthConfig({ auth, apiConfig, onAuthChange }) {
  const getMaskedValue = (value) => {
    if (!value) return '';
    if (value.length <= 4) return '*'.repeat(value.length);
    return '*'.repeat(value.length - 4) + value.slice(-4);
  };

  return (
    <div className="space-y-4">
      <label className="block text-sm font-medium text-gray-700">鉴权配置</label>
      <div className="space-y-3">
        <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
          <input
            type="radio"
            name="authType"
            value="inherit"
            checked={auth.type === 'inherit'}
            onChange={() => onAuthChange({ type: 'inherit' })}
            className="w-4 h-4 text-blue-600"
          />
          <div>
            <div className="text-sm font-medium text-gray-800">继承智能体鉴权</div>
            <div className="text-xs text-gray-500">
              使用顶部 API 基本信息配置的鉴权信息
              {apiConfig.authType === 'API_KEY' && apiConfig.apiKey && (
                <span className="ml-2 text-gray-400">（密钥：{getMaskedValue(apiConfig.apiKey)}）</span>
              )}
              {apiConfig.authType === 'BEARER_TOKEN' && apiConfig.bearerToken && (
                <span className="ml-2 text-gray-400">（Token：{getMaskedValue(apiConfig.bearerToken)}）</span>
              )}
            </div>
          </div>
        </label>

        <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
          <input
            type="radio"
            name="authType"
            value="custom"
            checked={auth.type === 'custom'}
            onChange={() => onAuthChange({ type: 'custom' })}
            className="w-4 h-4 text-blue-600"
          />
          <div className="flex-1">
            <div className="text-sm font-medium text-gray-800">独立配置</div>
            {auth.type === 'custom' && (
              <div className="mt-3 space-y-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">鉴权类型</label>
                  <select
                    value={auth.authSubType || 'API_KEY'}
                    onChange={(e) => onAuthChange({ authSubType: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                  >
                    <option value="API_KEY">API Key</option>
                    <option value="BEARER_TOKEN">Bearer Token</option>
                  </select>
                </div>
                {auth.authSubType !== 'BEARER_TOKEN' && (
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">API Key</label>
                    <input
                      type="password"
                      value={auth.apiKey}
                      onChange={(e) => onAuthChange({ apiKey: e.target.value })}
                      placeholder="请输入 API Key"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                    />
                  </div>
                )}
                {auth.authSubType !== 'API_KEY' && (
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Bearer Token</label>
                    <input
                      type="password"
                      value={auth.bearerToken}
                      onChange={(e) => onAuthChange({ bearerToken: e.target.value })}
                      placeholder="请输入 Bearer Token"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </label>

        <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
          <input
            type="radio"
            name="authType"
            value="none"
            checked={auth.type === 'none'}
            onChange={() => onAuthChange({ type: 'none' })}
            className="w-4 h-4 text-blue-600"
          />
          <div>
            <div className="text-sm font-medium text-gray-800">无需鉴权</div>
            <div className="text-xs text-gray-500">适用于内网接口或公开接口</div>
          </div>
        </label>
      </div>
    </div>
  );
}

// 模板侧边栏组件
function TemplateSidebar({ templates, currentIndex, onSelect, onAdd, onRemove, onRename }) {
  const [editingIndex, setEditingIndex] = useState(null);
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (editingIndex !== null && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingIndex]);

  const handleDoubleClick = (index, title) => {
    setEditingIndex(index);
    setEditValue(title);
  };

  const handleBlur = () => {
    if (editingIndex !== null && editValue.trim()) {
      onRename(editingIndex, editValue.trim());
    }
    setEditingIndex(null);
    setEditValue('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (editValue.trim()) {
        onRename(editingIndex, editValue.trim());
      }
      setEditingIndex(null);
      setEditValue('');
    } else if (e.key === 'Escape') {
      setEditingIndex(null);
      setEditValue('');
    }
  };

  const handleEditClick = (e, index, title) => {
    e.stopPropagation();
    setEditingIndex(index);
    setEditValue(title);
  };

  return (
    <div className="w-64 bg-white border-r border-gray-200 flex flex-col h-full">
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <div className="w-1 h-6 bg-blue-500 rounded-sm"></div>
          <h3 className="text-base font-bold text-[#333]">模板管理</h3>
        </div>
        <button
          onClick={onAdd}
          className="mt-3 w-full flex items-center justify-center gap-1 px-3 py-2 bg-blue-50 text-blue-600 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors"
        >
          <Plus className="w-4 h-4" />
          新增模板
        </button>
      </div>
      
      <div className="flex-1 overflow-y-auto p-2">
        <div className="space-y-1">
          {templates.map((template, index) => (
            <div
              key={template.id}
              className={`relative flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-all ${
                index === currentIndex
                  ? 'bg-blue-50 text-blue-700'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
              onClick={() => onSelect(index)}
              onDoubleClick={() => handleDoubleClick(index, template.title)}
            >
              {editingIndex === index ? (
                <input
                  ref={inputRef}
                  type="text"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onBlur={handleBlur}
                  onKeyDown={handleKeyDown}
                  className="flex-1 text-sm px-2 py-1 bg-white border-b-2 border-blue-500 outline-none"
                />
              ) : (
                <>
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                    index === currentIndex ? 'bg-blue-600' : 'bg-gray-300'
                  }`} />
                  <span className="text-sm truncate flex-1">{template.title}</span>
                  <div className="flex items-center gap-1 opacity-0 hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => handleEditClick(e, index, template.title)}
                      className="p-1 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded transition-colors"
                    >
                      <Edit3 className="w-3 h-3" />
                    </button>
                    {templates.length > 1 && (
                      <Popconfirm
                        title="确定删除此模板？"
                        onConfirm={(e) => { e.stopPropagation(); onRemove(index); }}
                        okText="确定"
                        cancelText="取消"
                      >
                        <button
                          className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </Popconfirm>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// 变量卡片组件
function VariableCard({ variable, index, variables, onUpdate, onRemove }) {
  const availableParents = variables
    .filter((v, i) => i !== index && (v.uiType === 'select' || v.uiType === 'select-cascade'))
    .map(v => v.name);

  const isCascadeType = variable.uiType === 'select-cascade';

  const handleUiTypeChange = (newType) => {
    const updates = {
      uiType: newType,
      optionSource: (newType === 'select' || newType === 'select-cascade') ? (variable.optionSource || 'manual') : (variable.optionSource || 'manual'),
    };
    if (newType !== 'select' && newType !== 'select-cascade') {
      updates.logicType = 'independent';
      updates.dependsOn = null;
      updates.cascadingMap = {};
    }
    if (newType === 'select-cascade') {
      updates.logicType = 'cascading';
    }
    onUpdate(index, updates);
  };

  const handleOptionChange = (optIndex, value) => {
    const newOptions = [...(variable.options || [])];
    newOptions[optIndex] = value;
    onUpdate(index, { options: newOptions });
  };

  const handleAddOption = () => {
    onUpdate(index, { options: [...(variable.options || []), ''] });
  };

  const handleRemoveOption = (optIndex) => {
    const newOptions = (variable.options || []).filter((_, i) => i !== optIndex);
    onUpdate(index, { options: newOptions });
  };

  // 手工映射级联：父值 → 子选项
  const cmap = variable.cascadingMap || {};
  const parentVar = variables.find(v => v.name === variable.dependsOn);
  const parentManualOptions = (parentVar?.optionSource !== 'api' ? (parentVar?.options || []) : []).filter(o => o && o.trim());
  const setChild = (pv, ci, val) => { const arr = [...(cmap[pv] || [])]; arr[ci] = val; onUpdate(index, { cascadingMap: { ...cmap, [pv]: arr } }); };
  const addChild = (pv) => onUpdate(index, { cascadingMap: { ...cmap, [pv]: [...(cmap[pv] || []), ''] } });
  const delChild = (pv, ci) => onUpdate(index, { cascadingMap: { ...cmap, [pv]: (cmap[pv] || []).filter((_, i) => i !== ci) } });

  return (
    <div className="p-4 bg-white rounded-lg border border-gray-200">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <span className="font-mono text-sm text-blue-600">{'{{' + variable.name + '}}'}</span>
          <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
            {variable.uiType === 'select' ? '下拉-独立' : 
             variable.uiType === 'select-cascade' ? '下拉-级联' :
             variable.uiType === 'text' ? '文本' :
             variable.uiType === 'number' ? '数字' : '日期'}
          </span>
        </div>
        <Popconfirm
          title="确定删除此变量？"
          onConfirm={() => onRemove(index)}
          okText="确定"
          cancelText="取消"
        >
          <button
            className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </Popconfirm>
      </div>

      <select
        value={variable.uiType}
        onChange={(e) => handleUiTypeChange(e.target.value)}
        className="text-sm px-3 py-1.5 border border-gray-300 rounded-lg w-full mb-3"
      >
        <option value="select">下拉-独立</option>
        <option value="select-cascade">下拉-级联</option>
        <option value="text">文本</option>
        <option value="number">数字</option>
        <option value="date">日期</option>
      </select>

      {(variable.uiType === 'select' || variable.uiType === 'select-cascade') && (
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">选项来源</label>
            <select
              value={variable.optionSource || 'manual'}
              onChange={(e) => onUpdate(index, { optionSource: e.target.value })}
              className="text-sm px-3 py-1.5 border border-gray-300 rounded-lg w-full"
            >
              <option value="manual">手工映射{isCascadeType ? '（父值→子选项）' : '（直接录入选项）'}</option>
              <option value="api">接口获取{isCascadeType ? '（实时拉取，父值作参数）' : '（实时拉取）'}</option>
            </select>
            {isCascadeType && (
              <p className="text-xs text-gray-500 mt-1">
                {variable.optionSource === 'api'
                  ? '选项多/常变时用接口获取，将父变量值作参数实时拉取'
                  : '选项少且稳定时用手工映射，为父变量每个值配置一组子选项'}
              </p>
            )}
          </div>

          {variable.uiType === 'select-cascade' && (
            <div>
              <label className="block text-xs text-gray-600 mb-1">依赖变量（父变量）</label>
              <select
                value={variable.dependsOn || ''}
                onChange={(e) => onUpdate(index, { dependsOn: e.target.value || null, cascadingMap: {} })}
                className="text-sm px-3 py-1.5 border border-gray-300 rounded-lg w-full"
              >
                <option value="">请选择依赖变量</option>
                {availableParents.map(v => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </div>
          )}

          {/* 手工映射级联：父值 → 子选项 */}
          {isCascadeType && variable.optionSource === 'manual' && variable.dependsOn && (
            parentManualOptions.length > 0 ? (
              <div className="space-y-2">
                <label className="text-xs text-gray-500">为父变量「{variable.dependsOn}」的每个值配置子选项</label>
                {parentManualOptions.map(pv => (
                  <div key={pv} className="p-2.5 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="text-xs font-medium text-gray-600 mb-1.5">
                      当 <span className="font-mono">{variable.dependsOn}</span> = <span className="text-blue-600">{pv}</span>
                    </div>
                    <div className="space-y-1.5">
                      {(cmap[pv] && cmap[pv].length ? cmap[pv] : ['']).map((c, ci) => (
                        <div key={ci} className="flex items-center gap-2">
                          <input
                            type="text"
                            value={c}
                            onChange={(e) => setChild(pv, ci, e.target.value)}
                            placeholder="子选项"
                            className="flex-1 text-sm px-2.5 py-1 border border-gray-300 rounded"
                          />
                          {(cmap[pv] || []).length > 1 && (
                            <button onClick={() => delChild(pv, ci)} className="p-1 text-gray-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                          )}
                        </div>
                      ))}
                      <button onClick={() => addChild(pv)} className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700">
                        <Plus className="w-3 h-3" /> 添加子选项
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
                父变量「{variable.dependsOn || '？'}」需为「手工映射」且已配置选项，才能在此逐值配置子选项；若父变量为接口获取，请将本变量改用「接口获取」。
              </div>
            )
          )}

          {variable.optionSource === 'manual' && !isCascadeType && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs text-gray-500">选项列表</label>
                <button
                  onClick={handleAddOption}
                  className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
                >
                  <Plus className="w-3 h-3" />
                  添加选项
                </button>
              </div>
              <div className="space-y-2">
                {(variable.options || []).map((option, optIndex) => (
                  <div key={optIndex} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={option}
                      onChange={(e) => handleOptionChange(optIndex, e.target.value)}
                      placeholder={`选项 ${optIndex + 1}`}
                      className="flex-1 text-sm px-3 py-1.5 border border-gray-300 rounded-lg"
                    />
                    {(variable.options || []).length > 1 && (
                      <button
                        onClick={() => handleRemoveOption(optIndex)}
                        className="p-1.5 text-gray-400 hover:text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {variable.optionSource === 'api' && (
            <div className="space-y-3 p-3 bg-gray-50 rounded-lg">
              <div>
                <label className="block text-xs text-gray-600 mb-1">接口地址</label>
                <input
                  type="text"
                  value={variable.apiUrl || ''}
                  onChange={(e) => onUpdate(index, { apiUrl: e.target.value })}
                  placeholder="https://api.example.com/options"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                />
              </div>
              {variable.uiType === 'select-cascade' && variable.dependsOn && (
                <div>
                  <label className="block text-xs text-gray-600 mb-1">接口参数（可使用 {'{{父变量名}}'}）</label>
                  <input
                    type="text"
                    value={variable.apiParams || ''}
                    onChange={(e) => onUpdate(index, { apiParams: e.target.value })}
                    placeholder={`parent_id={{${variable.dependsOn}}}`}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg font-mono"
                  />
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">选项值字段</label>
                  <input
                    type="text"
                    value={variable.apiValueField || ''}
                    onChange={(e) => onUpdate(index, { apiValueField: e.target.value })}
                    placeholder="value"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">选项标签字段</label>
                  <input
                    type="text"
                    value={variable.apiLabelField || ''}
                    onChange={(e) => onUpdate(index, { apiLabelField: e.target.value })}
                    placeholder="label"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                  />
                </div>
              </div>
              <AuthConfig
                auth={variable.apiAuth || { type: 'inherit' }}
                apiConfig={{}}
                onAuthChange={(updates) => onUpdate(index, { apiAuth: { ...variable.apiAuth, ...updates } })}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// 模板按钮组组件
function TemplateButtonGroup({ templates, currentIndex, onSelect }) {
  const [showMore, setShowMore] = useState(false);
  const maxVisible = 5;
  const visibleTemplates = templates.slice(0, maxVisible);
  const hiddenTemplates = templates.slice(maxVisible);

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {visibleTemplates.map((tpl, index) => (
        <button
          key={tpl.id}
          onClick={() => onSelect(index)}
          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
            index === currentIndex
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          {tpl.title}
        </button>
      ))}
      
      {hiddenTemplates.length > 0 && (
        <div className="relative">
          <button
            onClick={() => setShowMore(!showMore)}
            className="px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 transition-all flex items-center gap-1"
          >
            <MoreHorizontal className="w-3 h-3" />
            更多业务
            <ChevronDown className={`w-3 h-3 transition-transform ${showMore ? 'rotate-180' : ''}`} />
          </button>
          
          {showMore && (
            <>
              <div 
                className="fixed inset-0 z-10" 
                onClick={() => setShowMore(false)}
              />
              <div className="absolute top-full right-0 mt-1 min-w-[160px] bg-white rounded-lg shadow-lg border border-gray-200 z-20">
                <div className="p-1">
                  {hiddenTemplates.map((tpl, index) => (
                    <button
                      key={tpl.id}
                      onClick={() => {
                        onSelect(maxVisible + index);
                        setShowMore(false);
                      }}
                      className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 rounded transition-colors ${
                        maxVisible + index === currentIndex ? 'bg-blue-50 text-blue-600' : 'text-gray-600'
                      }`}
                    >
                      {tpl.title}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// 预览标签组件
function PreviewTag({ variable, value, options, isDisabled, onValueChange }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || '');
  const [showDropdown, setShowDropdown] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  useEffect(() => {
    setEditValue(value || '');
  }, [value]);

  const handleClick = () => {
    if (isDisabled) return;
    if (variable.uiType === 'text' || variable.uiType === 'number' || variable.uiType === 'date') {
      setIsEditing(true);
    } else {
      setShowDropdown(!showDropdown);
    }
  };

  const handleBlur = () => {
    if (variable.uiType === 'text' || variable.uiType === 'number' || variable.uiType === 'date') {
      onValueChange(variable.name, editValue.trim());
      setIsEditing(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      onValueChange(variable.name, editValue.trim());
      setIsEditing(false);
    } else if (e.key === 'Escape') {
      setEditValue(value || '');
      setIsEditing(false);
    }
  };

  const handleSelect = (selectedValue) => {
    onValueChange(variable.name, selectedValue);
    setShowDropdown(false);
  };

  const displayValue = value || '{{' + variable.name + '}}';
  const tagStyle = isDisabled
    ? 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-60'
    : value
      ? 'bg-blue-600 text-white'
      : 'bg-blue-50 text-blue-600 border border-blue-200';

  if ((variable.uiType === 'text' || variable.uiType === 'number' || variable.uiType === 'date') && isEditing) {
    return (
      <input
        ref={inputRef}
        type={variable.uiType === 'number' ? 'number' : variable.uiType === 'date' ? 'date' : 'text'}
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className="px-2 py-1 text-sm font-medium bg-white border-b-2 border-blue-500 outline-none"
        style={{ width: Math.max(80, editValue.length * 12 + 40) }}
        autoFocus
      />
    );
  }

  return (
    <div className="relative inline-block">
      <span
        onClick={handleClick}
        className={`px-3 py-1.5 text-sm font-medium rounded-lg cursor-pointer transition-all min-w-[100px] text-center ${tagStyle} ${isDisabled ? '' : 'hover:opacity-90'}`}
      >
        {displayValue}
        {!isDisabled && (
          <span className="ml-1 text-xs opacity-70">
            {(variable.uiType === 'text' || variable.uiType === 'number' || variable.uiType === 'date') ? '✎' : '▼'}
          </span>
        )}
      </span>

      {showDropdown && !isDisabled && (variable.uiType === 'select' || variable.uiType === 'select-cascade') && (
        <>
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setShowDropdown(false)}
          />
          <div className="absolute top-full left-0 mt-1 min-w-[160px] bg-white rounded-lg shadow-lg border border-gray-200 z-20">
            <div className="p-1">
              <button
                onClick={() => handleSelect('')}
                className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 rounded transition-colors ${!value ? 'bg-blue-50 text-blue-600' : 'text-gray-600'}`}
              >
                {'{{' + variable.name + '}}'}
              </button>
              {options.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleSelect(option)}
                  className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 rounded transition-colors ${value === option ? 'bg-blue-50 text-blue-600' : 'text-gray-600'}`}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// 后台配置模式
function LocalConfigMode({ config, methods }) {
  const {
    localTemplates,
    currentLocalTemplateIndex,
  } = config;

  const {
    updateLocalPromptTemplate,
    addLocalPromptVariable,
    removeLocalPromptVariable,
    updateLocalPromptVariable,
    updateLocalPreviewValue,
    addLocalTemplate,
    removeLocalTemplate,
    switchLocalTemplate,
    updateLocalTemplateTitle,
  } = methods;

  const currentTemplate = localTemplates[currentLocalTemplateIndex] || { title: '', template: '', variables: [], previewValues: {} };
  const { title, template, variables, previewValues } = currentTemplate;

  const extractVariables = useCallback((text) => {
    const regex = /\{\{(\w+)\}\}/g;
    const matches = [];
    let match;
    while ((match = regex.exec(text)) !== null) {
      if (!matches.includes(match[1])) {
        matches.push(match[1]);
      }
    }
    return matches;
  }, []);

  useEffect(() => {
    const extractedVars = extractVariables(template);
    
    extractedVars.forEach(variableName => {
      const existingVar = variables.find(v => v.name === variableName);
      if (!existingVar) {
        addLocalPromptVariable({
          name: variableName,
          uiType: 'select',
          logicType: 'independent',
          optionSource: 'manual',
          dependsOn: null,
          options: [],
          cascadingMap: {},
        });
      }
    });

    variables.forEach((variable, index) => {
      if (!extractedVars.includes(variable.name)) {
        removeLocalPromptVariable(index);
      }
    });
  }, [template, variables, addLocalPromptVariable, removeLocalPromptVariable, extractVariables]);

  const handlePreviewValueChange = (variableName, value) => {
    const variable = variables.find(v => v.name === variableName);
    if (!variable) return;

    if ((variable.uiType === 'select-cascade') && variable.dependsOn) {
      const parentValue = previewValues[variable.dependsOn];
      if (!parentValue) return;
    }

    updateLocalPreviewValue(variableName, value);

    if (value !== previewValues[variableName]) {
      const childVars = variables.filter(v => v.dependsOn === variableName);
      childVars.forEach(child => {
        updateLocalPreviewValue(child.name, '');
        const grandChildren = variables.filter(v => v.dependsOn === child.name);
        grandChildren.forEach(gc => updateLocalPreviewValue(gc.name, ''));
      });
    }
  };

  const getCascadingOptions = (variable) => {
    if (variable.uiType !== 'select' && variable.uiType !== 'select-cascade') return [];
    if (variable.uiType !== 'select-cascade' || !variable.dependsOn) {
      return variable.options || [];
    }
    const parentValue = previewValues[variable.dependsOn];
    if (!parentValue) return [];
    return variable.cascadingMap?.[parentValue] || [];
  };

  const isVariableDisabled = (variable) => {
    if (variable.uiType !== 'select-cascade' || !variable.dependsOn) return false;
    return !previewValues[variable.dependsOn];
  };

  const getFinalPrompt = () => {
    if (!template) return '';
    let result = template;
    variables.forEach(v => {
      const value = previewValues[v.name];
      if (value) {
        result = result.replace(new RegExp(`\\{\\{${v.name}\\}\\}`, 'g'), value);
      }
    });
    return result;
  };

  return (
    <div className="flex h-[calc(100vh-240px)]">
      <TemplateSidebar
        templates={localTemplates}
        currentIndex={currentLocalTemplateIndex}
        onSelect={switchLocalTemplate}
        onAdd={addLocalTemplate}
        onRemove={removeLocalTemplate}
        onRename={updateLocalTemplateTitle}
      />

      <div className="flex-1 flex">
        <div className="w-1/2 border-r border-gray-200 overflow-y-auto">
          <div className="p-6 space-y-6">
            <div className="flex items-center gap-2">
              <div className="w-1 h-6 bg-blue-500 rounded-sm"></div>
              <h3 className="text-base font-bold text-[#333]">内容配置</h3>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">模板标题</label>
              <input
                type="text"
                value={title}
                onChange={(e) => updateLocalPromptTemplate({ title: e.target.value })}
                placeholder="请输入模板标题，如：需求文档编写"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">话术模板</label>
              <textarea
                value={template}
                onChange={(e) => updateLocalPromptTemplate({ template: e.target.value })}
                placeholder="请输入话术模板，变量使用 {{变量名}} 格式"
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all resize-none"
              />
              <p className="text-xs text-gray-500 mt-1">提示：输入 {'{{变量名}}'} 格式的变量，系统会自动识别并添加到变量列表中</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                <Settings className="w-4 h-4" />
                变量定义
              </label>

              {variables.length === 0 ? (
                <div className="text-center text-gray-400 py-8 border-2 border-dashed border-gray-200 rounded-lg bg-white">
                  <div className="text-4xl mb-4">📋</div>
                  <p className="text-sm">暂无变量</p>
                  <p className="text-xs mt-1 text-gray-500">在话术模板中使用 {'{{变量名}}'} 格式添加变量</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {variables.map((variable, index) => (
                    <VariableCard
                      key={variable.name}
                      variable={variable}
                      index={index}
                      variables={variables}
                      onUpdate={updateLocalPromptVariable}
                      onRemove={removeLocalPromptVariable}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="w-1/2 bg-gray-50">
          <div className="h-full flex flex-col">
            <div className="bg-white border-b border-gray-200 p-4">
              <div className="flex items-center gap-2 mb-4">
                <Eye className="w-4 h-4 text-blue-600" />
                <h4 className="text-sm font-medium text-gray-800">交互预览</h4>
              </div>
              
              <div className="bg-gradient-to-br from-blue-50 via-white to-indigo-50 rounded-xl p-4 mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h5 className="text-sm font-medium text-gray-800">智能助手</h5>
                    <p className="text-xs text-gray-500">基于大模型的网络运维助手</p>
                  </div>
                </div>
              </div>

              <TemplateButtonGroup
                templates={localTemplates}
                currentIndex={currentLocalTemplateIndex}
                onSelect={switchLocalTemplate}
              />
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {!template ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                  <div className="text-4xl mb-4">📝</div>
                  <p className="text-sm font-medium">请输入话术模板</p>
                  <p className="text-xs text-gray-500 mt-2">变量使用 {'{{变量名}}'} 格式</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex flex-wrap items-center gap-2 min-h-[56px]">
                      {template.split(/(\{\{\w+\}\})/g).map((part, index) => {
                        const varMatch = part.match(/\{\{(\w+)\}\}/);
                        if (varMatch) {
                          const variableName = varMatch[1];
                          const variable = variables.find(v => v.name === variableName);
                          if (!variable) return <span key={index} className="text-gray-700 text-sm">{part}</span>;

                          return (
                            <PreviewTag
                              key={variableName}
                              variable={variable}
                              value={previewValues[variableName]}
                              options={getCascadingOptions(variable)}
                              isDisabled={isVariableDisabled(variable)}
                              onValueChange={handlePreviewValueChange}
                            />
                          );
                        }
                        return <span key={index} className="text-gray-700 text-sm">{part}</span>;
                      })}
                    </div>
                  </div>

                  <div className="bg-gray-100 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-medium text-gray-600">最终 Prompt 预览</span>
                    </div>
                    <p className="text-sm text-gray-700 font-mono break-all">{getFinalPrompt() || '等待配置...'}</p>
                  </div>

                  <div className="text-xs text-gray-500 flex flex-wrap gap-2">
                    {variables.map((v, i) => (
                      <span key={i} className="bg-gray-100 px-2 py-1 rounded">
                        <span className="font-medium text-gray-600">{'{{' + v.name + '}}'}</span>
                        <span className="text-gray-400 ml-1">
                          ({v.uiType === 'select' ? '下拉独立' : 
                            v.uiType === 'select-cascade' ? '下拉级联' :
                            v.uiType === 'text' ? '文本' :
                            v.uiType === 'number' ? '数字' : '日期'})
                        </span>
                        {v.dependsOn && <span className="text-gray-400 ml-1">→ 依赖{v.dependsOn}</span>}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// 接口拉取模式
function ApiConfigMode({ config, methods }) {
  const { apiConfigMode, apiConfig } = config;
  const { 
    updateApiConfigMode, 
    updateApiConfigAuth, 
    updateApiConfigFieldMapping,
    addApiConfigParam,
    updateApiConfigParam,
    removeApiConfigParam
  } = methods;
  const [testResults, setTestResults] = useState(null);
  const [isTesting, setIsTesting] = useState(false);

  const handleTestApi = async () => {
    setIsTesting(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      setTestResults({
        success: true,
        data: [
          { id: 1, title: '模板1', template: '这是模板1的内容 {{var1}}', variables: [{ name: 'var1', type: 'text' }] },
          { id: 2, title: '模板2', template: '这是模板2的内容 {{var2}}', variables: [{ name: 'var2', type: 'select' }] },
        ],
        message: '接口调用成功'
      });
    } catch (error) {
      setTestResults({ success: false, message: '接口调用失败' });
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <div className="flex h-[calc(100vh-240px)]">
      {/* 左栏：模板列表（只读） */}
      <div className="w-64 bg-white border-r border-gray-200 flex flex-col h-full">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Database className="w-5 h-5 text-green-600" />
            <h3 className="text-base font-bold text-[#333]">接口模板</h3>
          </div>
          <div className="flex items-center gap-1 mt-2">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-xs text-green-600">实时同步中</span>
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto p-2">
          {testResults?.data ? (
            <div className="space-y-1">
              {testResults.data.map((template, index) => (
                <div
                  key={template.id}
                  className="px-3 py-2 rounded-lg text-gray-700 hover:bg-gray-50 cursor-pointer"
                >
                  <div className="text-sm truncate">{template.title}</div>
                  <div className="text-xs text-gray-400 truncate">{template.template}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-gray-400 py-8">
              <div className="text-2xl mb-2">🗄️</div>
              <p className="text-sm">点击右侧"接口测试"</p>
              <p className="text-xs">预览返回的模板列表</p>
            </div>
          )}
        </div>
      </div>

      {/* 中栏：接口配置 */}
      <div className="w-1/2 border-r border-gray-200 overflow-y-auto">
        <div className="p-6 space-y-6">
          <div className="flex items-center gap-2">
            <Link2 className="w-5 h-5 text-blue-600" />
            <h3 className="text-base font-bold text-[#333]">接口配置</h3>
          </div>

          {/* 接口地址 */}
          <div className="space-y-3">
            <div className="flex gap-2">
              <select
                value={apiConfigMode.method}
                onChange={(e) => updateApiConfigMode({ method: e.target.value })}
                className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium"
              >
                <option value="GET">GET</option>
                <option value="POST">POST</option>
              </select>
              <input
                type="text"
                value={apiConfigMode.url}
                onChange={(e) => updateApiConfigMode({ url: e.target.value })}
                placeholder="https://api.example.com/templates"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
          </div>

          {/* 鉴权配置 */}
          <AuthConfig
            auth={apiConfigMode.auth}
            apiConfig={apiConfig}
            onAuthChange={updateApiConfigAuth}
          />

          {/* 请求参数 */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700">请求参数</label>
              <button
                onClick={addApiConfigParam}
                className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
              >
                <Plus className="w-3 h-3" />
                添加参数
              </button>
            </div>
            <div className="space-y-2">
              {apiConfigMode.params.map((param, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={param.key}
                    onChange={(e) => updateApiConfigParam(index, { key: e.target.value })}
                    placeholder="参数名"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                  <input
                    type="text"
                    value={param.value}
                    onChange={(e) => updateApiConfigParam(index, { value: e.target.value })}
                    placeholder="值（支持 {{user_id}}, {{agent_id}}）"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
                  />
                  <button
                    onClick={() => removeApiConfigParam(index)}
                    className="p-2 text-gray-400 hover:text-red-500"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* 缓存时长 */}
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">缓存时长（秒）</label>
            <input
              type="number"
              value={apiConfigMode.cacheDuration}
              onChange={(e) => updateApiConfigMode({ cacheDuration: parseInt(e.target.value) || 300 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>

          {/* 字段映射 */}
          <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-700">字段映射</label>
            <div className="p-4 bg-gray-50 rounded-lg space-y-4">
              <div className="text-xs text-gray-500 mb-3">模板层</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">列表路径</label>
                  <input
                    type="text"
                    value={apiConfigMode.fieldMapping.listPath}
                    onChange={(e) => updateApiConfigFieldMapping('listPath', e.target.value)}
                    placeholder="data.list"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">标题字段</label>
                  <input
                    type="text"
                    value={apiConfigMode.fieldMapping.titleField}
                    onChange={(e) => updateApiConfigFieldMapping('titleField', e.target.value)}
                    placeholder="title"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">话术语段</label>
                  <input
                    type="text"
                    value={apiConfigMode.fieldMapping.templateField}
                    onChange={(e) => updateApiConfigFieldMapping('templateField', e.target.value)}
                    placeholder="template"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">变量字段</label>
                  <input
                    type="text"
                    value={apiConfigMode.fieldMapping.variablesField}
                    onChange={(e) => updateApiConfigFieldMapping('variablesField', e.target.value)}
                    placeholder="variables"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                  />
                </div>
              </div>

              <div className="text-xs text-gray-500 mb-3 pt-2 border-t border-gray-200">变量层</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">变量名字段</label>
                  <input
                    type="text"
                    value={apiConfigMode.fieldMapping.variable.nameField}
                    onChange={(e) => updateApiConfigFieldMapping('variable.nameField', e.target.value)}
                    placeholder="name"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">类型字段</label>
                  <input
                    type="text"
                    value={apiConfigMode.fieldMapping.variable.typeField}
                    onChange={(e) => updateApiConfigFieldMapping('variable.typeField', e.target.value)}
                    placeholder="type"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">父变量字段</label>
                  <input
                    type="text"
                    value={apiConfigMode.fieldMapping.variable.parentField}
                    onChange={(e) => updateApiConfigFieldMapping('variable.parentField', e.target.value)}
                    placeholder="parent"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">静态选项字段</label>
                  <input
                    type="text"
                    value={apiConfigMode.fieldMapping.variable.staticOptionsField}
                    onChange={(e) => updateApiConfigFieldMapping('variable.staticOptionsField', e.target.value)}
                    placeholder="options"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs text-gray-600 mb-1">动态选项接口字段</label>
                  <input
                    type="text"
                    value={apiConfigMode.fieldMapping.variable.dynamicOptionsApiField}
                    onChange={(e) => updateApiConfigFieldMapping('variable.dynamicOptionsApiField', e.target.value)}
                    placeholder="optionsApi"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* 兜底策略 */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">兜底策略</label>
            <div className="space-y-3">
              <select
                value={apiConfigMode.fallback.strategy}
                onChange={(e) => updateApiConfigMode({ fallback: { ...apiConfigMode.fallback, strategy: e.target.value } })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="hide">失败时隐藏</option>
                <option value="fallback">失败时使用本地配置</option>
              </select>
              <div>
                <label className="block text-xs text-gray-600 mb-1">超时时间（毫秒）</label>
                <input
                  type="number"
                  value={apiConfigMode.fallback.timeout}
                  onChange={(e) => updateApiConfigMode({ fallback: { ...apiConfigMode.fallback, timeout: parseInt(e.target.value) || 5000 } })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
            </div>
          </div>

          {/* 测试按钮 */}
          <div className="pt-4">
            <button
              onClick={handleTestApi}
              disabled={isTesting}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              <TestTube className="w-4 h-4" />
              {isTesting ? '测试中...' : '接口测试 + 预览渲染'}
            </button>
            {testResults && (
              <div className={`mt-3 p-3 rounded-lg text-sm ${testResults.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                {testResults.message}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 右栏：预览 */}
      <div className="w-1/2 bg-gray-50">
        <div className="h-full flex flex-col">
          <div className="bg-white border-b border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-4">
              <Eye className="w-4 h-4 text-blue-600" />
              <h4 className="text-sm font-medium text-gray-800">接口模板预览</h4>
            </div>
            
            <div className="bg-gradient-to-br from-blue-50 via-white to-indigo-50 rounded-xl p-4 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h5 className="text-sm font-medium text-gray-800">智能助手</h5>
                  <p className="text-xs text-gray-500">基于接口模板的网络运维助手</p>
                </div>
              </div>
            </div>

            {testResults?.data && (
              <TemplateButtonGroup
                templates={testResults.data}
                currentIndex={0}
                onSelect={() => {}}
              />
            )}
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {!testResults?.data ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                <div className="text-4xl mb-4">🗄️</div>
                <p className="text-sm font-medium">先点击"接口测试"</p>
                <p className="text-xs text-gray-500 mt-2">预览接口返回的模板和变量</p>
              </div>
            ) : (
              <div className="space-y-4">
                {testResults.data.map((tpl, idx) => (
                  <div key={tpl.id} className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="text-sm font-medium text-gray-800 mb-2">{tpl.title}</div>
                    <p className="text-sm text-gray-600">{tpl.template}</p>
                    {tpl.variables?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {tpl.variables.map((v, i) => (
                          <span key={i} className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded">
                            {'{{' + v.name + '}}'}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// 主组件
export default function PromptTemplateConfig() {
  const {
    config,
    setPromptTemplateMode,
    updateLocalPromptTemplate,
    addLocalPromptVariable,
    removeLocalPromptVariable,
    updateLocalPromptVariable,
    updateLocalPreviewValue,
    addLocalTemplate,
    removeLocalTemplate,
    switchLocalTemplate,
    updateLocalTemplateTitle,
    updateApiConfigMode,
    updateApiConfigAuth,
    updateApiConfigFieldMapping,
    addApiConfigParam,
    updateApiConfigParam,
    removeApiConfigParam,
  } = useConfig();

  const methods = {
    updateLocalPromptTemplate,
    addLocalPromptVariable,
    removeLocalPromptVariable,
    updateLocalPromptVariable,
    updateLocalPreviewValue,
    addLocalTemplate,
    removeLocalTemplate,
    switchLocalTemplate,
    updateLocalTemplateTitle,
    updateApiConfigMode,
    updateApiConfigAuth,
    updateApiConfigFieldMapping,
    addApiConfigParam,
    updateApiConfigParam,
    removeApiConfigParam,
  };

  return (
    <Card
      variant="outlined"
      className="border border-[#e8e8e8] rounded-[4px] overflow-hidden"
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <div className="w-1 h-6 bg-blue-500 rounded-sm"></div>
          <h3 className="text-base font-bold text-[#333]">快捷提示词模板配置</h3>
          <span className="text-xs text-gray-400 ml-2">话术由后台配置；下拉变量的选项可手工录入或从业务接口实时获取</span>
        </div>
      </div>

      <LocalConfigMode config={config} methods={methods} />
    </Card>
  );
}
